/**
 * Connector Configuration Dialog Component
 * Phase 2: Form Builder Integration
 *
 * Dynamic, schema-driven connector configuration using Atlas Form Builder
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useConnectorConfigStore } from './connectorConfigStore';
import { ModalPortal } from './ModalPortal';
import { SettingsIcon, DocumentIcon, GlobeIcon, BrainIcon, SearchIcon, PlugIcon, LightbulbIcon, ZapIcon, PencilIcon, ChevronDownIcon } from '@bizfirst/common-react';
import { NodeLeftIcon, NodeBrandImage } from '../NodeIcon';
import {
  connectorApiClient,
  nodeFormApiClient,
  knowledgeBaseApiClient,
  channelApiClient,
  llmModelApiClient,
  agentFunctionApiClient,
  apiConfig,
  NODE_SUB_USAGE,
  type NodeFormDto,
} from '@flow-studio/api';
import { AiAgentAdminApiProvider, AgentPicker, CredentialPicker, IdentityKeyManager, TrustedDIDManager } from '@bizfirst/ai-agent-admin-ui';
import '@bizfirst/ai-agent-admin-ui/styles';
import { AtlasCredentialFormAdapter, AtlasCredentialOAuthConnect } from './AtlasCredentialFormAdapter';
import { ENABLE_FORM_LABEL_MENU, FormLabelMenu, FlatFormTabList } from './formLabelMenu';
import type { AcceptedCredentialType } from '@flow-studio/types';
import { Loader } from '../Shared/Loader';
import { KnowledgeBaseEditor } from '../Entities/KnowledgeBase/KnowledgeBaseEditor';
import { ChannelEditor } from '../Entities/Channel/ChannelEditor';
import { LLMModelEditor } from '../Entities/LLMModel/LLMModelEditor';
import { AIFunctionEditor } from '../Entities/AIFunction/AIFunctionEditor';
import { getVisibleTabs, getTabDisplayProperties, type TabName } from './tabVisibilityConditions';

// did-admin's URL (port 6128, matches its vite.config.ts) — this package doesn't depend on
// @passport/app-urls, so mirror its env-var-with-fallback convention locally rather than add a
// cross-package dependency for one link. Keep DID_ADMIN_APP_URL's port here in sync with
// @passport/app-urls/src/AppUrls.ts if either ever changes.
const DID_ADMIN_APP_URL = import.meta.env.VITE_DID_ADMIN_APP_URL || 'http://localhost:6128';
import type {
  Connector,
  TestConnectorConnectionRequest,
  TestConnectorConnectionResponseData,
  FormSchema,
  KnowledgeBase,
  Channel,
  LlmModel,
  AgentFunction,
} from '@flow-studio/api';

type FormValues = Record<string, any>;

/** Recursively decode HTML entities that FormRenderer encodes in string values (e.g. &quot; → "). */
function decodeHtmlEntities(value: any): any {
  if (typeof value === 'string') {
    return value
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g,  "'")
      .replace(/&amp;/g,  '&')
      .replace(/&lt;/g,   '<')
      .replace(/&gt;/g,   '>');
  }
  if (Array.isArray(value)) return value.map(decodeHtmlEntities);
  if (value && typeof value === 'object') {
    const out: Record<string, any> = {};
    for (const k of Object.keys(value)) out[k] = decodeHtmlEntities(value[k]);
    return out;
  }
  return value;
}
import type {
  ConfigurationData,
} from './types';

import { FormRenderer, FormThemeProvider } from '@atlas-forms/player-components-react';
import '@atlas-forms/player-components-react/dist/controls.css';
import '@atlas-forms/player-components-react/dist/form-field-view.css';
import '@atlas-forms/player-components-react/dist/file-upload.css';
import '@atlas-forms/player-components-react/dist/grid.css';
import type { FormSchema as AtlasFormSchema } from '@atlas-forms/types-js';
import { useUIStore } from '@flow-studio/store';
import { validateRequiredFields, ValidationError } from './connectorValidation';
import './ConnectorConfigDialog.css';

export interface NodeInfo {
  id: string;
  type: string;
  backendId?: number;
  data: {
    displayName: string;
    description?: string;
    category: string;
    connectorId?: number;
    icon?: string;
    headerImageUrl?: string;
    helpUrl?: string;
  };
}

interface ConnectorConfigDialogProps {
  connectorId?: number;
  processElementId?: number; // numeric ProcessElement ID from backend for node-specific form loading
  isOpen: boolean;
  onClose: () => void;
  onSave?: (connector: Connector) => void;
  onNodeInfoSave?: (displayName: string, description: string) => void;
  /** Called instead of the connector API path when there is no connectorId (logic nodes) */
  onFormValuesSave?: (values: Record<string, any>) => void;
  /** Pre-saved form values for logic nodes (e.g. processElement.parameters) — restored on reopen */
  initialFormValues?: Record<string, any>;
  nodeInfo?: NodeInfo;
  // Tab visibility context - optional properties from the node
  nodeCategory?: string;
  isTrigger?: boolean;
  isManual?: boolean;
  triggerType?: string; // 'webhook', 'schedule', etc.
  /** When the node declares acceptedCredentialTypes, use CredentialSection (type-aware) instead of generic CredentialSelector */
  acceptedCredentialTypes?: AcceptedCredentialType[];
}

export const ConnectorConfigDialog: React.FC<ConnectorConfigDialogProps> = ({
  connectorId,
  processElementId,
  isOpen,
  onClose,
  onSave,
  onNodeInfoSave,
  onFormValuesSave,
  initialFormValues,
  nodeInfo,
  nodeCategory = '',
  isTrigger = false,
  isManual = false,
  triggerType = '',
  acceptedCredentialTypes,
}) => {
  const { theme } = useUIStore();
  const store = useConnectorConfigStore();
  // Keep a stable ref to the store so effects can call actions without
  // listing the whole store object as a dep (it changes on every state update).
  const storeRef = useRef(store);
  storeRef.current = store;

  // Reset form state whenever the dialog opens
  useEffect(() => {
    if (!isOpen) return;
    storeRef.current.resetFormState();
  }, [isOpen]);

  // Compute visible tabs — memoised so the array reference is stable
  const visibleTabs = useMemo(
    () => getVisibleTabs({ nodeCategory, isTrigger, isManual, triggerType }),
    [nodeCategory, isTrigger, isManual, triggerType],
  );

  // Resolve acceptedCredentialTypes with fallback chain:
  // 1. connector.configuration.acceptedCredentialTypes (saved on the connector — highest priority, template-seeded)
  // 2. acceptedCredentialTypes prop (NodeType level from workflowStore.nodeTypes)
  // 3. undefined → CredentialSelector fallback
  const resolvedCredentialTypes = useMemo<AcceptedCredentialType[] | undefined>(() => {
    if (store.connector?.Configuration) {
      try {
        const cfg = typeof store.connector.Configuration === 'string'
          ? JSON.parse(store.connector.Configuration)
          : store.connector.Configuration;
        // Handle both camelCase (string stored) and PascalCase (object after convertKeysToPascalCase)
        const raw = cfg?.acceptedCredentialTypes ?? cfg?.AcceptedCredentialTypes;
        if (Array.isArray(raw) && raw.length > 0) {
          return raw.map((t: any) => ({
            code: t.code ?? t.Code,
            editFormID: t.editFormID ?? t.EditFormID,
            viewFormID: t.viewFormID ?? t.ViewFormID,
          })) as AcceptedCredentialType[];
        }
      } catch { /* ignore parse error */ }
    }
    return acceptedCredentialTypes;
  }, [store.connector?.Configuration, acceptedCredentialTypes]);

  // Re-populate visible tabs every time the dialog opens.
  // resetFormState() clears the store's visibleTabs on open; without isOpen in
  // the deps the effect only fires when the computed visibleTabs value changes
  // (i.e. props changed), so reopening with the same nodeCategory left the
  // store tabs empty and the Configuration/Credentials/Help header never appeared.
  useEffect(() => {
    if (!isOpen) return;
    if (visibleTabs.length > 0) {
      storeRef.current.setVisibleTabs(visibleTabs);
      storeRef.current.setActiveTab(visibleTabs[0]);
    }
  }, [isOpen, visibleTabs]);

  // Sync documentation from nodeInfo when dialog opens
  useEffect(() => {
    if (!isOpen) return;
    if (nodeInfo?.data.displayName) {
      storeRef.current.setDocLabel(nodeInfo.data.displayName);
    }
    if (nodeInfo?.data.description != null) {
      storeRef.current.setDocDescription(nodeInfo.data.description);
    }
  }, [isOpen, nodeInfo?.data.displayName, nodeInfo?.data.description]);

  useEffect(() => {
    const schemaLoaded = !!store.formSchema;
    const hasControls = (store.formSchema?.controls?.length ?? 0) > 0;

    // Find which form this schema belongs to
    const matchingForm = store.configForms.find(f => f.schema === store.formSchema);

    console.log('%c[ConnectorConfigDialog] 🗂️ Active Tab Changed', 'color: #4a90e2; font-weight: bold;', {
      activeTab: store.activeTab,
      activeConfigFormId: store.activeConfigFormId,
      matchingFormDetails: matchingForm ? {
        formID: matchingForm.formID,
        formName: matchingForm.formName,
        formCode: matchingForm.formCode,
        source: matchingForm.source,
      } : 'NOT FOUND',
      formSchemaLoaded: schemaLoaded,
      hasControls,
      controlsCount: store.formSchema?.controls?.length ?? 0,
      schemaKeys: store.formSchema ? Object.keys(store.formSchema) : null,
      processElementId: store.processElementId,
      connectorId: store.connectorId,
    });

    if (store.formSchema && !hasControls) {
      console.error('%c⚠️ CRITICAL: Schema loaded but has NO controls', 'color: #e74c3c; font-weight: bold; font-size: 12px;');
      console.error('Form Details:', {
        formID: matchingForm?.formID ?? 'UNKNOWN',
        formName: matchingForm?.formName ?? 'UNKNOWN',
        formCode: matchingForm?.formCode ?? 'UNKNOWN',
        source: matchingForm?.source ?? 'UNKNOWN',
      });
      console.error('Schema JSON (Pretty):\n', JSON.stringify(store.formSchema, null, 2));
      console.error('All ConfigForms from API:\n', JSON.stringify(store.configForms.map(f => ({
        formID: f.formID,
        formName: f.formName,
        formCode: f.formCode,
        source: f.source,
        displayOrder: f.displayOrder,
        hasSchema: !!f.schema,
        schemaControlsCount: f.schema?.controls?.length ?? 0,
      })), null, 2));
    }
  }, [store.activeTab, store.formSchema]);

  // Memory search is local-only; all other entity search/show state lives in connectorConfigStore
  const [memorySearch, setMemorySearch] = useState('');
  const [memoryShowResults, setMemoryShowResults] = useState(false);

  // Entity add dialog state
  const [entityDialogAction, setEntityDialogAction] = useState<'create' | 'edit'>('create');

  // Config forms state (multi-tab support for node-specific forms)
  const [configForms, setConfigForms] = useState<NodeFormDto[]>([]);
  const [activeConfigFormId, setActiveConfigFormId] = useState<number | null>(null);
  const [configFormValues, setConfigFormValues] = useState<Map<number, Record<string, any>>>(new Map());

  // Initialize form when dialog opens — runs for both connector nodes (connectorId) and logic nodes (processElementId only)
  useEffect(() => {
    if (!isOpen) return;
    if (!connectorId && !processElementId) return;

    if (connectorId) {
      storeRef.current.setConnectorId(connectorId);
      storeRef.current.setOpen(true);
    }

    let cancelled = false;
    initializeForm(() => cancelled);

    return () => { cancelled = true; };
  }, [isOpen, connectorId, processElementId]);

  // Initialize entity IDs from connector data when it loads.
  // PRIORITY: Process_ProcessElements.Configuration (surfaced here via the initialFormValues prop,
  // i.e. the node's own nodeFormValues) is the actual runtime source of truth for these fields —
  // OctopusAgentIDInfo/OctopusLlmInfo/etc. resolve agentID/credentialID/... from a ConfigDataPropertyBag
  // backed by ProcessElement.Configuration (see IOctopusAiAgentNodeExecutorSettings' own doc comment:
  // "all agent-specific configuration properties read from ProcessElement.Configuration JSON"); they
  // never read Connector.Configuration or the Connector table's own FK columns. Without this priority,
  // reopening the config dialog for an already-configured, already-executing node showed every picker as
  // empty (because Connector.Configuration/columns are never the field the runtime actually populated),
  // which read as "my selection didn't save" even though the process-element-level value was intact.
  // Connector.Configuration / Connector.<Column> remain as fallbacks for any older data that predates
  // this fix, or for node types that do bind through the Connector row instead.
  useEffect(() => {
    const connector = storeRef.current.connector;
    if (connector) {
      const cfg = parseConfiguration(connector.Configuration);
      const pe = (initialFormValues ?? {}) as Record<string, any>;
      const resolvedAgentId        = pe.agentID         ?? cfg.agentID        ?? connector.AgentID        ?? null;
      const resolvedKnowledgeBaseId = pe.knowledgeBaseID ?? cfg.knowledgeBaseID ?? connector.KnowledgeBaseID ?? null;
      const resolvedChannelId       = pe.channelID       ?? cfg.channelID       ?? connector.ChannelID       ?? null;
      const resolvedLlmModelId      = pe.llmModelID      ?? cfg.llmModelID      ?? connector.LlmModelID      ?? null;
      const resolvedAgentFunctionId = pe.agentFunctionID ?? cfg.agentFunctionID ?? connector.AgentFunctionID ?? null;
      const resolvedCredentialId    = pe.credentialID    ?? cfg.credentialID    ?? connector.CredentialID    ?? null;
      const resolvedMemoryType      = pe.memoryType      ?? cfg.memoryType      ?? null;
      if (resolvedAgentId)         storeRef.current.setAgentId(resolvedAgentId);
      if (resolvedKnowledgeBaseId) storeRef.current.setKnowledgeBaseId(resolvedKnowledgeBaseId);
      if (resolvedChannelId)       storeRef.current.setChannelId(resolvedChannelId);
      if (resolvedLlmModelId)      storeRef.current.setLlmModelId(resolvedLlmModelId);
      if (resolvedAgentFunctionId) storeRef.current.setAgentFunctionId(resolvedAgentFunctionId);
      if (resolvedCredentialId)    storeRef.current.setCredentialID(resolvedCredentialId);
      if (resolvedMemoryType === 'bizfirst' || resolvedMemoryType === 'custom') storeRef.current.setMemoryType(resolvedMemoryType);
    }
  }, [store.connector, initialFormValues]);

  // Load entities silently when IDs are set but data is empty
  // (Agent's own pre-load moved into AgentPicker itself — see that component's useEffect on `value`.)
  useEffect(() => {
    if (store.llmModelId && store.llmModels.length === 0) {
      loadLLMModels(false);
    }
  }, [store.llmModelId, store.llmModels.length]);

  useEffect(() => {
    if (store.knowledgeBaseId && store.knowledgeBases.length === 0) {
      loadKnowledgeBases(false);
    }
  }, [store.knowledgeBaseId, store.knowledgeBases.length]);

  useEffect(() => {
    if (store.channelId && store.channels.length === 0) {
      loadChannels(false);
    }
  }, [store.channelId, store.channels.length]);

  useEffect(() => {
    if (store.agentFunctionId && store.agentFunctions.length === 0) {
      loadAgentFunctions(false);
    }
  }, [store.agentFunctionId, store.agentFunctions.length]);

  // Auto-load entity lookups when tabs are activated
  // (Agent tab no longer participates here — AgentPicker owns its own on-demand loading.)
  useEffect(() => {
    if (store.activeTab === 'knowledgeBase' && store.knowledgeBases.length === 0) {
      loadKnowledgeBases(false);
    } else if (store.activeTab === 'channel' && store.channels.length === 0) {
      loadChannels(false);
    } else if (store.activeTab === 'llmModel' && store.llmModels.length === 0) {
      loadLLMModels(false);
    } else if (store.activeTab === 'agentFunction' && store.agentFunctions.length === 0) {
      loadAgentFunctions(false);
    }
  }, [store.activeTab]);

  /**
   * Load connector data from API
   */
  const loadConnectorData = async () => {
    store.setLoading(true);
    store.setError(null);
    store.setTestResult(null);

    try {
      const response = await connectorApiClient.getById({
        id: connectorId!,
      });

      if (response.success && response.data) {
        store.setConnector(response.data);
      } else {
        const errorMsg = response.error?.message || 'Failed to load connector';
        throw new Error(errorMsg);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load connector data';
      store.setError(errorMessage);
    } finally {
      store.setLoading(false);
    }
  };

  /**
   * Load form schema and set initial values via the Atlas Forms hook.
   * isCancelled() is checked after each await so StrictMode's double-invoke
   * doesn't result in duplicate API calls or stale state updates.
   */
  const initializeForm = async (isCancelled: () => boolean = () => false) => {
    storeRef.current.setLoading(true);
    storeRef.current.setError(null);
    storeRef.current.setConfigForms([]);
    storeRef.current.setActiveConfigFormId(null);
    let schema: FormSchema | null = null;
    let forms: NodeFormDto[] = [];
    let connectorData = store.connector;

    if (processElementId) {
      try {
        const response = await nodeFormApiClient.getNodeFormsBySubUsage(
          processElementId,
          NODE_SUB_USAGE.NodeDesignConfiguration
        );

        if (isCancelled()) { storeRef.current.setLoading(false); return; }

        if (!response.success) {
          const msg = response.error?.message || 'Failed to load node configuration forms.';
          storeRef.current.setError(msg);
          storeRef.current.setLoading(false);
          return;
        }

        if (response.data && response.data.length > 0) {
          forms = [...response.data].sort((a, b) => a.displayOrder - b.displayOrder);
          schema = forms[0].schema as unknown as FormSchema;
          store.setConfigForms(forms);
          // Do NOT call setActiveConfigFormId here — defer until after initialValues are set
          // so the FormRenderer mounts once with the correct data (not empty then refilled).
        } else {
          // No forms returned - show error with specific error code
          const errorCode = 'node-forms-did-zero-forms';
          const msg = `No node forms found for process element [Code: ${errorCode}]`;
          storeRef.current.setError(msg);
          console.error(`[ConnectorConfigDialog] ${msg}`);
          storeRef.current.setLoading(false);
          return;
        }
      } catch (error: unknown) {
        if (isCancelled()) { storeRef.current.setLoading(false); return; }
        const msg = error instanceof Error ? error.message : 'Failed to load node configuration forms.';
        storeRef.current.setError(msg);
        storeRef.current.setLoading(false);
        return;
      }
    }

    // TIER 2–4 only apply when a connector is involved
    if (connectorId) {
      // TIER 2: Always fetch the authoritative connector record from the server.
      // A cached store.connector can be a stale/incomplete placeholder (e.g. missing
      // CredentialID after certain refresh flows) — trusting it here made the
      // Credentials tab show "— Select or create —" even when a credential was
      // correctly saved, because the dropdown reads store.credentialID, which was
      // never re-hydrated from the real record.
      try {
        const response = await connectorApiClient.getById({ id: connectorId });
        if (isCancelled()) return;
        if (response.success && response.data) {
          connectorData = response.data;
          store.setConnector(response.data);
        }
      } catch (error: unknown) {
        if (isCancelled()) return;
      }

      // No fallback to FormBuilder or default schemas — only use node-specific forms from TIER 1
    }

    if (isCancelled()) {
      storeRef.current.setLoading(false);
      return;
    }

    store.setFormSchema(schema as any);

    // For logic nodes (no connector): restore from pe.configuration.nodeFormValues
    // which survives workflow save (PS_ProcessElements.Configuration).
    if (!connectorData) {
      if (isCancelled()) return;
      const merged = decodeHtmlEntities({ ...(initialFormValues ?? {}) });
      if (Object.keys(merged).length > 0) {
        store.setLoadedConfigValues(merged);
        store.setFormRendererInitialValues(merged);
      }
      // Restore agent function metadata saved inside nodeFormValues
      if (merged.agentFunctionDisplayName !== undefined) storeRef.current.setAgentFunctionDisplayName(String(merged.agentFunctionDisplayName || ''));
      if (merged.agentFunctionDisplayOrder !== undefined) storeRef.current.setAgentFunctionDisplayOrder(Number(merged.agentFunctionDisplayOrder) || 0);
      if (merged.agentFunctionLabel !== undefined) storeRef.current.setAgentFunctionLabel(String(merged.agentFunctionLabel || ''));
      if (merged.agentFunctionFlowPath !== undefined) storeRef.current.setAgentFunctionFlowPath(String(merged.agentFunctionFlowPath || ''));
      if (merged.agentFunctionAlias !== undefined) storeRef.current.setAgentFunctionAlias(String(merged.agentFunctionAlias || ''));
      if (merged.agentFunctionProfileName !== undefined) storeRef.current.setAgentFunctionProfileName(String(merged.agentFunctionProfileName || ''));
      // Restore entity IDs and memory type from nodeFormValues (logic nodes have no connector row)
      if (merged.agentID)         storeRef.current.setAgentId(Number(merged.agentID));
      if (merged.knowledgeBaseID) storeRef.current.setKnowledgeBaseId(Number(merged.knowledgeBaseID));
      if (merged.channelID)       storeRef.current.setChannelId(Number(merged.channelID));
      if (merged.llmModelID)      storeRef.current.setLlmModelId(Number(merged.llmModelID));
      if (merged.agentFunctionID) storeRef.current.setAgentFunctionId(Number(merged.agentFunctionID));
      if (merged.credentialID)    storeRef.current.setCredentialID(Number(merged.credentialID));
      if (merged.memoryType === 'bizfirst' || merged.memoryType === 'custom') storeRef.current.setMemoryType(merged.memoryType);
      // Activate the first tab AFTER initial values are set so FormRenderer mounts with data.
      if (forms.length > 0) store.setActiveConfigFormId(forms[0].formID);
      storeRef.current.setLoading(false);
      return;
    }

    // Decode HTML entities from stored configuration — FormRenderer encodes " to &quot;
    // during save; decode here so the form displays real characters on load.
    const configurationData = decodeHtmlEntities(parseConfiguration(connectorData.Configuration));

    // CredentialID: PRIORITY: process-element Configuration (initialFormValues — the runtime source
    // OctopusLlmInfo actually reads via ConfigDataPropertyBag) → connector Configuration JSON → direct
    // column. See the matching priority and rationale in the "Initialize entity IDs from connector
    // data" effect above.
    const resolvedCredentialId = (initialFormValues as any)?.credentialID ?? configurationData.credentialID ?? connectorData.CredentialID ?? null;
    if (resolvedCredentialId) store.setCredentialID(Number(resolvedCredentialId));

    // Node-specific form schemas (e.g. the HIL Form node) can reuse the control id
    // "description" for their own field (e.g. "Instructions"). Only force the generic
    // Connector.Description column in when there's no node-specific schema claiming
    // that key — otherwise it clobbers the schema's value with the unrelated,
    // usually-empty connector description on every reopen after the first save.
    const hasNodeSpecificForms = forms.length > 0;
    const initialValues = decodeHtmlEntities({
      ...configurationData,
      ...(initialFormValues ?? {}),
      name: connectorData.Name || '',
      ...(hasNodeSpecificForms ? {} : { description: connectorData.Description || '' }),
      baseUrl: connectorData.BaseUrl || '',
      apiEndPoint: connectorData.ApiEndPoint || '',
      timeout: connectorData.Timeout || 30000,
      retryCount: connectorData.RetryCount || 3,
    });

    // Stable fallback used by handleConfigFormTabChange so tab switches always
    // restore saved values even before the user has interacted with a tab.
    store.setLoadedConfigValues(initialValues);
    store.setFormRendererInitialValues(initialValues);
    // Activate the first tab AFTER initial values are set so FormRenderer mounts
    // once with the correct data (key change triggers remount).
    if (forms.length > 0) store.setActiveConfigFormId(forms[0].formID);

    // Restore agent function metadata — configurationData (connector JSON) takes precedence
    // over initialFormValues (nodeFormValues), which is the fallback for non-admin users
    // whose connector API update fails so fields only reach pe.configuration.nodeFormValues.
    const afSource = { ...(initialFormValues ?? {}), ...configurationData };
    if (afSource.agentFunctionDisplayName !== undefined) storeRef.current.setAgentFunctionDisplayName(String(afSource.agentFunctionDisplayName || ''));
    if (afSource.agentFunctionDisplayOrder !== undefined) storeRef.current.setAgentFunctionDisplayOrder(Number(afSource.agentFunctionDisplayOrder) || 0);
    if (afSource.agentFunctionLabel !== undefined) storeRef.current.setAgentFunctionLabel(String(afSource.agentFunctionLabel || ''));
    if (afSource.agentFunctionFlowPath !== undefined) storeRef.current.setAgentFunctionFlowPath(String(afSource.agentFunctionFlowPath || ''));
    if (afSource.agentFunctionAlias !== undefined) storeRef.current.setAgentFunctionAlias(String(afSource.agentFunctionAlias || ''));
    if (afSource.agentFunctionProfileName !== undefined) storeRef.current.setAgentFunctionProfileName(String(afSource.agentFunctionProfileName || ''));
    // Restore entity IDs — PRIORITY: initialFormValues (Process_ProcessElements.Configuration, the
    // actual runtime source of truth per OctopusAgentIDInfo/OctopusLlmInfo/etc.) over configurationData
    // (Connector.Configuration, which the executor never reads for these fields). Same priority as the
    // "Initialize entity IDs from connector data" effect above — kept in sync so a fresh load and a
    // store.connector-change both resolve the picker's value identically.
    const peValuesInit = (initialFormValues ?? {}) as Record<string, any>;
    const resolvedAgentIdInit         = peValuesInit.agentID         ?? configurationData.agentID;
    const resolvedKnowledgeBaseIdInit = peValuesInit.knowledgeBaseID ?? configurationData.knowledgeBaseID;
    const resolvedChannelIdInit       = peValuesInit.channelID       ?? configurationData.channelID;
    const resolvedLlmModelIdInit      = peValuesInit.llmModelID      ?? configurationData.llmModelID;
    const resolvedAgentFunctionIdInit = peValuesInit.agentFunctionID ?? configurationData.agentFunctionID;
    const resolvedMemoryTypeInit      = peValuesInit.memoryType      ?? configurationData.memoryType;
    if (resolvedAgentIdInit)         storeRef.current.setAgentId(Number(resolvedAgentIdInit));
    if (resolvedKnowledgeBaseIdInit) storeRef.current.setKnowledgeBaseId(Number(resolvedKnowledgeBaseIdInit));
    if (resolvedChannelIdInit)       storeRef.current.setChannelId(Number(resolvedChannelIdInit));
    if (resolvedLlmModelIdInit)      storeRef.current.setLlmModelId(Number(resolvedLlmModelIdInit));
    if (resolvedAgentFunctionIdInit) storeRef.current.setAgentFunctionId(Number(resolvedAgentFunctionIdInit));
    if (resolvedMemoryTypeInit === 'bizfirst' || resolvedMemoryTypeInit === 'custom') storeRef.current.setMemoryType(resolvedMemoryTypeInit);
    storeRef.current.setLoading(false);
  };

  const handleSave = async () => {
    store.setSaving(true);
    store.setError(null);
    try {
      const vals = Object.keys(store.formRendererValues).length > 0
        ? store.formRendererValues
        : store.formRendererInitialValues;

      // No connector — persist via onFormValuesSave → pe.configuration.nodeFormValues → workflow save
      if (!store.connector) {
        if (onFormValuesSave) {
          const merged: Record<string, any> = { ...store.loadedConfigValues };
          store.configFormValues.forEach((tabVals) => { Object.assign(merged, tabVals); });
          Object.assign(merged, vals);
          Object.assign(merged, {
            agentFunctionDisplayName: store.agentFunctionDisplayName,
            agentFunctionDisplayOrder: store.agentFunctionDisplayOrder,
            agentFunctionLabel: store.agentFunctionLabel,
            agentFunctionFlowPath: store.agentFunctionFlowPath,
            agentFunctionAlias: store.agentFunctionAlias,
            agentFunctionProfileName: store.agentFunctionProfileName,
          });
          // Inject entity IDs selected on the Agent/Knowledge Base/Channel/LLM Model/
          // Agent Function/Credentials tabs — there is no connector row for logic
          // nodes, so these live only in the store and must be merged in here.
          if (store.agentId)         merged.agentID = store.agentId;
          if (store.knowledgeBaseId) merged.knowledgeBaseID = store.knowledgeBaseId;
          if (store.channelId)       merged.channelID = store.channelId;
          if (store.llmModelId)      merged.llmModelID = store.llmModelId;
          if (store.agentFunctionId) merged.agentFunctionID = store.agentFunctionId;
          if (store.credentialID)   merged.credentialID = store.credentialID;
          if (store.memoryType)     merged.memoryType = store.memoryType;
          onFormValuesSave(merged);
          useUIStore.getState().showToast('success', 'Configuration saved');
        }
        onClose();
        return;
      }

      // Validate required fields before API call
      // Use the same fallback logic as the save: form value first, then existing connector value
      const resolvedName = vals.name?.trim() || store.connector.Name?.trim() || store.docLabel?.trim();
      const resolvedBaseUrl = vals.baseUrl?.trim() || store.connector.BaseUrl;
      const { valid, errors } = validateRequiredFields(
        store.connector.ConnectorID || store.connectorId,
        resolvedName,
        resolvedBaseUrl
      );

      if (!valid) {
        const errorMsg = Object.entries(errors)
          .map(([field, error]) => `${field}: ${error}`)
          .join('; ');
        throw new ValidationError({}, errorMsg);
      }

      const afMetaSave = {
        agentFunctionDisplayName: store.agentFunctionDisplayName,
        agentFunctionDisplayOrder: store.agentFunctionDisplayOrder,
        agentFunctionLabel: store.agentFunctionLabel,
        agentFunctionFlowPath: store.agentFunctionFlowPath,
        agentFunctionAlias: store.agentFunctionAlias,
        agentFunctionProfileName: store.agentFunctionProfileName,
      };
      // Merge values from all config form tabs.
      // Start from loadedConfigValues so fields from tabs never visited are preserved.
      let configurationValuesSave = vals;
      if (store.configForms.length > 0) {
        const mergedTabs: Record<string, any> = { ...store.loadedConfigValues };
        store.configFormValues.forEach((tabVals) => { Object.assign(mergedTabs, tabVals); });
        Object.assign(mergedTabs, vals);
        configurationValuesSave = mergedTabs;
      }

      // Inject credentialId into Configuration JSON for executors that read it from config (e.g. Gmail).
      if (store.credentialID) {
        configurationValuesSave = { ...configurationValuesSave, credentialID: store.credentialID };
      }

      // Inject all entity IDs into Configuration JSON — binding is via Configuration, not direct columns
      const resolvedAgentIdSave        = store.agentId        || store.connector.AgentID        || null;
      const resolvedKnowledgeBaseIdSave = store.knowledgeBaseId || store.connector.KnowledgeBaseID || null;
      const resolvedChannelIdSave       = store.channelId       || store.connector.ChannelID       || null;
      const resolvedLlmModelIdSave      = store.llmModelId      || store.connector.LlmModelID      || null;
      const resolvedAgentFunctionIdSave = store.agentFunctionId || store.connector.AgentFunctionID || null;
      if (resolvedAgentIdSave)         configurationValuesSave = { ...configurationValuesSave, agentID:        resolvedAgentIdSave };
      if (resolvedKnowledgeBaseIdSave) configurationValuesSave = { ...configurationValuesSave, knowledgeBaseID: resolvedKnowledgeBaseIdSave };
      if (resolvedChannelIdSave)       configurationValuesSave = { ...configurationValuesSave, channelID:       resolvedChannelIdSave };
      if (resolvedLlmModelIdSave)      configurationValuesSave = { ...configurationValuesSave, llmModelID:      resolvedLlmModelIdSave };
      if (resolvedAgentFunctionIdSave) configurationValuesSave = { ...configurationValuesSave, agentFunctionID: resolvedAgentFunctionIdSave };
      if (store.memoryType)           configurationValuesSave = { ...configurationValuesSave, memoryType: store.memoryType };

      const connectorId = store.connector.ConnectorID || store.connectorId;
      const baseCfgSave = serializeConfiguration(configurationValuesSave, store.configForms.length > 0);
      const mergedCfgSave = JSON.stringify({
        ...JSON.parse(baseCfgSave || '{}'),
        ...afMetaSave,
      });

      const updatedConnector: Connector = {
        ...store.connector,
        ConnectorID: connectorId,
        Name: resolvedName,
        Description: vals.description?.trim() || store.connector.Description,
        BaseUrl: resolvedBaseUrl,
        ApiEndPoint: vals.apiEndPoint?.trim() || store.connector.ApiEndPoint,
        Timeout: typeof vals.timeout === 'number' ? vals.timeout : store.connector.Timeout,
        RetryCount: typeof vals.retryCount === 'number' ? vals.retryCount : store.connector.RetryCount,
        CredentialID: store.credentialID ?? store.connector.CredentialID,
        AgentID: resolvedAgentIdSave ?? undefined,
        KnowledgeBaseID: resolvedKnowledgeBaseIdSave ?? undefined,
        ChannelID: resolvedChannelIdSave ?? undefined,
        LlmModelID: resolvedLlmModelIdSave ?? undefined,
        AgentFunctionID: resolvedAgentFunctionIdSave ?? undefined,
        Configuration: mergedCfgSave,
      };

      const response = await connectorApiClient.update(updatedConnector);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to save connector');
      }

      const saved = response.data ?? updatedConnector;
      store.setConnector(saved);
      onSave?.(saved);

      if (onFormValuesSave) {
        const nodeVals: Record<string, any> = { ...store.loadedConfigValues };
        store.configFormValues.forEach((tabVals) => { Object.assign(nodeVals, tabVals); });
        Object.assign(nodeVals, configurationValuesSave);
        Object.assign(nodeVals, {
          agentFunctionDisplayName: store.agentFunctionDisplayName,
          agentFunctionDisplayOrder: store.agentFunctionDisplayOrder,
          agentFunctionLabel: store.agentFunctionLabel,
          agentFunctionFlowPath: store.agentFunctionFlowPath,
          agentFunctionAlias: store.agentFunctionAlias,
          agentFunctionProfileName: store.agentFunctionProfileName,
        });
        onFormValuesSave(nodeVals);
      }

      if (onNodeInfoSave && (store.docLabel !== nodeInfo?.data.displayName || store.docDescription !== (nodeInfo?.data.description ?? ''))) {
        onNodeInfoSave(store.docLabel, store.docDescription);
      }

      useUIStore.getState().showToast('success', 'Connector saved successfully');
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save connector';
      store.setError(msg);
      useUIStore.getState().showToast('error', msg);
      console.error('[ConnectorConfigDialog] Save error:', err);
    } finally {
      store.setSaving(false);
    }
  };

  /**
   * Handle config form tab change
   * Saves current tab's values and switches to new tab's schema
   */
  const handleConfigFormTabChange = (formID: number) => {
    const form = store.configForms.find(f => f.formID === formID);
    if (!form) return;

    if (store.activeConfigFormId !== null) {
      // formRendererValues is {} until the user interacts. Use any previously
      // saved values for this tab (or loadedConfigValues) to avoid wiping data.
      const currentValues = Object.keys(store.formRendererValues).length > 0
        ? store.formRendererValues
        : (store.configFormValues.get(store.activeConfigFormId) ?? store.loadedConfigValues);
      store.setConfigFormValues(store.activeConfigFormId, currentValues);
    }

    store.setActiveConfigFormId(formID);
    // Only update the rendered schema when a valid schema is available.
    // A missing schema (schema not yet configured) switches the tab but
    // leaves the FormRenderer hidden rather than blocking the click entirely.
    if (form.schema) {
      store.setFormSchema(form.schema as any);
    } else {
      store.setFormSchema(null as any);
    }

    // Restore saved values for this tab, falling back to the full loaded config
    // so the form is never blank when switching to a tab for the first time.
    const saved = store.configFormValues.get(formID);
    store.setFormRendererInitialValues(saved ?? store.loadedConfigValues);
  };

  /**
   * Load knowledge bases (with optional dropdown display)
   */
  const loadKnowledgeBases = async (showResults: boolean = true) => {
    store.setLoadingKBs(true);
    store.setKbError(null);
    try {
      const response = await knowledgeBaseApiClient.getActive();
      if (response.success && response.data) {
        store.setKnowledgeBases(response.data);
        if (showResults) {
          store.setKbShowResults(true);
        }
      } else {
        const errorMsg = response.error?.message || 'Failed to load knowledge bases';
        store.setKbError(errorMsg);
      }
    } catch (err: unknown) {
      console.error('[ConnectorConfigDialog] Error loading knowledge bases:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to load knowledge bases';
      store.setKbError(errorMsg);
    } finally {
      store.setLoadingKBs(false);
    }
  };

  /**
   * Filter knowledge bases by search text
   */
  const filterKnowledgeBases = (searchText: string) => {
    store.setKbSearch(searchText);
    if (!searchText.trim()) {
      if (store.knowledgeBases.length > 0) {
        store.setKbShowResults(true);
      }
    } else {
      store.setKbShowResults(true);
    }
  };

  /**
   * Get filtered knowledge bases based on search text
   */
  const filteredKnowledgeBases = store.kbSearch.trim()
    ? store.knowledgeBases.filter((kb) => kb.Name?.toLowerCase().includes(store.kbSearch.toLowerCase()))
    : store.knowledgeBases;

  /**
   * Load channels (with optional dropdown display)
   */
  const loadChannels = async (showResults: boolean = true) => {
    store.setLoadingChannels(true);
    store.setChannelError(null);
    try {
      const response = await channelApiClient.getActive();
      if (response.success && response.data) {
        store.setChannels(response.data);
        if (showResults) {
          store.setChannelShowResults(true);
        }
      } else {
        const errorMsg = response.error?.message || 'Failed to load channels';
        store.setChannelError(errorMsg);
      }
    } catch (err: unknown) {
      console.error('[ConnectorConfigDialog] Error loading channels:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to load channels';
      store.setChannelError(errorMsg);
    } finally {
      store.setLoadingChannels(false);
    }
  };

  /**
   * Filter channels by search text
   */
  const filterChannels = (searchText: string) => {
    store.setChannelSearch(searchText);
    if (!searchText.trim()) {
      if (store.channels.length > 0) {
        store.setChannelShowResults(true);
      }
    } else {
      store.setChannelShowResults(true);
    }
  };

  /**
   * Get filtered channels based on search text
   */
  const filteredChannels = store.channelSearch.trim()
    ? store.channels.filter((c) => {
        const name = c.ChannelName || c.Name || '';
        return name.toLowerCase().includes(store.channelSearch.toLowerCase());
      })
    : store.channels;

  /**
   * Load LLM models (with optional dropdown display)
   */
  const loadLLMModels = async (showResults: boolean = true) => {
    store.setLoadingLLMs(true);
    store.setLlmError(null);
    try {
      const response = await llmModelApiClient.getActive();
      if (response.success && response.data) {
        store.setLlmModels(response.data);
        if (showResults) {
          store.setLlmShowResults(true);
        }
      } else {
        const errorMsg = response.error?.message || 'Failed to load LLM models';
        store.setLlmError(errorMsg);
      }
    } catch (err: unknown) {
      console.error('[ConnectorConfigDialog] Error loading LLM models:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to load LLM models';
      store.setLlmError(errorMsg);
    } finally {
      store.setLoadingLLMs(false);
    }
  };

  /**
   * Filter LLM models by search text
   */
  const filterLLMModels = (searchText: string) => {
    store.setLlmSearch(searchText);
    if (!searchText.trim()) {
      if (store.llmModels.length > 0) {
        store.setLlmShowResults(true);
      }
    } else {
      store.setLlmShowResults(true);
    }
  };

  /**
   * Get filtered LLM models based on search text
   */
  const filteredLLMModels = store.llmSearch.trim()
    ? store.llmModels.filter((m) => {
        const name = m.ModelName || m.Name || '';
        return name.toLowerCase().includes(store.llmSearch.toLowerCase());
      })
    : store.llmModels;

  /**
   * Load agent functions (with optional dropdown display)
   */
  const loadAgentFunctions = async (showResults: boolean = true) => {
    store.setLoadingFunctions(true);
    store.setFunctionError(null);
    try {
      const response = await agentFunctionApiClient.getActive();
      if (response.success && response.data) {
        store.setAgentFunctions(response.data);
        if (showResults) {
          store.setFunctionShowResults(true);
        }
      } else {
        const errorMsg = response.error?.message || 'Failed to load agent functions';
        store.setFunctionError(errorMsg);
      }
    } catch (err: unknown) {
      console.error('[ConnectorConfigDialog] Error loading agent functions:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to load agent functions';
      store.setFunctionError(errorMsg);
    } finally {
      store.setLoadingFunctions(false);
    }
  };

  /**
   * Filter agent functions by search text
   */
  const filterAgentFunctions = (searchText: string) => {
    store.setFunctionSearch(searchText);
    if (!searchText.trim()) {
      if (store.agentFunctions.length > 0) {
        store.setFunctionShowResults(true);
      }
    } else {
      store.setFunctionShowResults(true);
    }
  };

  /**
   * Get filtered agent functions based on search text
   */
  const filteredAgentFunctions = store.functionSearch.trim()
    ? store.agentFunctions.filter((f) => f.Name?.toLowerCase().includes(store.functionSearch.toLowerCase()))
    : store.agentFunctions;

  /**
   * Handle knowledge base added from dialog
   */
  const handleKnowledgeBaseAdded = (kb: unknown): void => {
    const kbData = kb as { KnowledgeBaseID?: number };
    if (kbData.KnowledgeBaseID) {
      store.setKnowledgeBaseId(kbData.KnowledgeBaseID);
      loadKnowledgeBases();
    }
  };

  /**
   * Handle channel added from dialog
   */
  const handleChannelAdded = (channel: unknown): void => {
    const channelData = channel as { ChannelID?: number };
    if (channelData.ChannelID) {
      store.setChannelId(channelData.ChannelID);
      loadChannels();
    }
  };

  /**
   * Handle LLM model added from dialog
   */
  const handleLLMModelAdded = (model: unknown): void => {
    const modelData = model as { LlmModelID?: number };
    if (modelData.LlmModelID) {
      store.setLlmModelId(modelData.LlmModelID);
      loadLLMModels();
    }
  };

  /**
   * Handle agent function added from dialog
   */
  const handleAgentFunctionAdded = (agentFunction: unknown): void => {
    const funcData = agentFunction as { AgentFunctionID?: number };
    if (funcData.AgentFunctionID) {
      store.setAgentFunctionId(funcData.AgentFunctionID);
      loadAgentFunctions();
    }
  };

  /**
   * Handle test connection button click
   */
  const handleTestConnection = async () => {
    if (!store.connector) return;

    store.setTesting(true);
    store.setError(null);
    store.setTestResult(null);

    try {
      const request: TestConnectorConnectionRequest = {
        ConnectorID: connectorId,
      } as any;

      const response = await connectorApiClient.testConnection(request);

      if (response.success && response.data) {
        store.setTestResult(response.data);
      } else {
        store.setTestResult(response.data ?? null);
        store.setError(response.error?.message || 'Connection test failed');
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to test connection';
      store.setError(errorMsg);
    } finally {
      store.setTesting(false);
    }
  };

  /**
   * Get default form schema (Phase 2 - basic schema)
   * TODO: Phase 2 - Load from backend API
   */
  /**
   * Fallback schema used when API returns no forms.
   * This ensures the dialog always works, even if:
   * - processElementId is not provided
   * - Node-specific forms are not configured
   * - Legacy form-builder API calls also return nothing
   *
   * This is the ultimate safety net for connector configuration.
   */
  /**
   * Extract form title from tab control in schema, or fall back to NodeFormDto title
   * Priority: Tab control label → NodeFormDto.title → NodeFormDto.formName
   *
   * Allows form schema to define its own display title, which takes precedence over
   * the database-stored title. If no tab control found, falls back to NodeFormDto.Title,
   * then FormName.
   *
   * @param form The NodeFormDto
   * @returns Display title for the form tab
   */
  const getFormDisplayTitle = (form: NodeFormDto): string => {
    // Try to get title from tab control in the form schema
    if (form.schema && typeof form.schema === 'object') {
      const schema = form.schema as FormSchema;
      const tabControl = schema.controls?.find(
        (control) => (control.type as string) === 'tab' || (control.type as string) === 'tabs'
      );
      if (tabControl && (tabControl as any).label) {
        return (tabControl as any).label;
      }
    }

    // Fall back to NodeFormDto title (from Atlas_Forms.Title column)
    if (form.title) {
      return form.title;
    }

    // Ultimate fallback to formName (internal code name)
    return form.formName;
  };

  const getDefaultSchema = () => {
    return {
      version: '1.0',
      metadata: {
        formID: 'connector-config',
        title: 'Connector Configuration',
        description: 'Configure connector settings',
      },
      layout: {
        type: 'single-column',
        spacing: 'normal',
        showLabelsAbove: true,
      },
      controls: [
        {
          id: 'name',
          type: 'text',
          label: 'Name',
          placeholder: 'Enter connector name',
          required: true,
          order: 1,
          validation: {
            required: true,
            minLength: 3,
            maxLength: 100,
          },
        },
        {
          id: 'description',
          type: 'textarea',
          label: 'Description',
          placeholder: 'Enter connector description',
          order: 2,
          rows: 3,
        },
        {
          id: 'baseUrl',
          type: 'text',
          label: 'Base URL',
          placeholder: 'https://api.example.com',
          helpText: 'The base URL of the API endpoint',
          order: 3,
          validation: {
            pattern: '^https?://.*',
          },
        },
        {
          id: 'apiEndPoint',
          type: 'text',
          label: 'API Endpoint',
          placeholder: '/api/v1/endpoint',
          helpText: 'The specific API endpoint path',
          order: 4,
        },
        {
          id: 'timeout',
          type: 'number',
          label: 'Timeout (ms)',
          defaultValue: 30000,
          order: 5,
          validation: {
            min: 1000,
            max: 300000,
          },
        },
        {
          id: 'retryCount',
          type: 'number',
          label: 'Retry Count',
          defaultValue: 3,
          order: 6,
          validation: {
            min: 0,
            max: 10,
          },
        },
      ],
    };
  };

  /**
   * Parse configuration JSON string
   */
  const parseConfiguration = (jsonConfig: string | null | undefined): ConfigurationData => {
    if (!jsonConfig) return {};
    try {
      return JSON.parse(jsonConfig) as ConfigurationData;
    } catch {
      console.warn('[ConnectorConfigDialog] Invalid configuration JSON');
      return {};
    }
  };

  /**
   * Serialize configuration to JSON string for Connector.Configuration field
   * This should contain only custom fields, not standard connector fields
   *
   * @param hasNodeSpecificForms When true, the node owns a schema-driven form (e.g. the
   *   HIL Form node) whose control ids can legitimately collide with the generic connector
   *   field names below (e.g. "description" meaning "Instructions"). In that case those
   *   fields belong to the node's own configuration, not the connector's dedicated columns,
   *   so they must not be stripped out.
   */
  const serializeConfiguration = (formData: FormValues, hasNodeSpecificForms = false): string => {
    // Standard connector fields that have dedicated database columns
    // NOTE: credentialID is intentionally NOT excluded — the backend reads it from
    // the Configuration JSON via config["credentialID"] in RegisterCredentialsFromConfig.
    const standardFields = hasNodeSpecificForms
      ? ['configuration', 'connector']
      : [
          'name',
          'description',
          'baseUrl',
          'apiEndPoint',
          'timeout',
          'retryCount',
          'configuration', // The nested configuration object itself
          'connector', // The full connector object
        ];

    // If form has a configuration object, use it directly
    if (formData.configuration && typeof formData.configuration === 'object') {
      return JSON.stringify(formData.configuration);
    }

    // Otherwise, extract custom fields from root level
    const customFields: ConfigurationData = {};
    Object.keys(formData).forEach((key) => {
      if (!standardFields.includes(key)) {
        customFields[key] = formData[key];
      }
    });

    return JSON.stringify(customFields);
  };

  // TODO: replace `true` with actual tenant-admin role check once auth context is wired up
  const canDesignForms = true;

  const getFormDesignerUrl = (formID: number): string => {
    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';

    if (isLocalhost) {
      const devBase =
        (import.meta.env.VITE_FORM_STUDIO_DEV_URL as string | undefined) ||
        'http://localhost:5175';
      return `${devBase.replace(/\/$/, '')}/?view=designer&formID=${formID}`;
    }

    const prodPath =
      (import.meta.env.VITE_FORM_STUDIO_PATH as string | undefined) ||
      '/formstudio/';
    return `${window.location.origin}${prodPath.startsWith('/') ? prodPath : '/' + prodPath}?view=designer&formID=${formID}`;
  };

  const handleOpenFormDesigner = (formID: number) => {
    window.open(getFormDesignerUrl(formID), '_blank', 'noopener,noreferrer');
  };

  // Memoize decoded values so FormRenderer doesn't see a new object reference on
  // every render — without this, decodeHtmlEntities creates a new object each render,
  // FormRenderer fires onValuesChange, which updates the store, causing an infinite loop.
  const decodedInitialValues = useMemo(
    () => decodeHtmlEntities(store.formRendererInitialValues),
    [store.formRendererInitialValues],
  );

  const handleFormValuesChange = useCallback(
    (v: Record<string, any>) => store.setFormRendererValues(decodeHtmlEntities(v)),
    // store.setFormRendererValues is a stable Zustand action reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  if (!isOpen) return null;

  return (
    // AgentPicker/CredentialPicker (Agent/Credentials tabs below) need this context. baseUrl/
    // tenantId come from the same apiConfig flow-studio-api's own baseApiClient already uses;
    // getAuthToken reads the same localStorage 'authToken' key baseApiClient.ts reads directly
    // (see that file's Authorization-header logic) — not @passport/store, since this package is
    // deliberately host-agnostic and flow-studio's own API clients predate the SSO/@passport
    // migration (see App.tsx's SsoAuthGate mirrorLegacyAuthTokenKey="authToken" comment).
    <AiAgentAdminApiProvider
      baseUrl={apiConfig.baseUrl}
      tenantId={apiConfig.tenantId}
      getAuthToken={() => localStorage.getItem('authToken')}
      formRenderer={AtlasCredentialFormAdapter}
      oauthConnect={AtlasCredentialOAuthConnect}
    >
    <ModalPortal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal modal-wide connector-config-dialog" onClick={(e) => e.stopPropagation()} onContextMenu={(e) => e.stopPropagation()}>
          {/* Header — title+close on top row, tabs on second row */}
          <div className="modal-header-plain">
            <div className="modal-header-top-row">
              <div className="modal-title">
                <NodeLeftIcon icon={nodeInfo?.data?.icon} category={nodeInfo?.data?.category || ''} size={18} color="currentColor" />
                <span>{nodeInfo?.data?.displayName ?? 'Configure Connector'}</span>
                <NodeBrandImage headerImageUrl={nodeInfo?.data?.headerImageUrl} size={20} style={{ marginLeft: '6px' }} />
              </div>
              <button className="modal-close-button" onClick={onClose}>×</button>
            </div>
            {!store.loading && store.visibleTabs.length > 0 && (
              <div className="tabs-navigation">
                {store.visibleTabs.map((tabName) => {
                  const tabProps = getTabDisplayProperties(tabName as TabName);
                  return (
                    <button
                      key={tabName}
                      className={`tab-button ${store.activeTab === tabName ? 'active' : ''}`}
                      onClick={() => store.setActiveTab(tabName)}
                    >
                      {tabProps.icon} {tabProps.label}
                    </button>
                  );
                })}
                {nodeInfo?.data?.helpUrl && (
                  <a
                    href={nodeInfo.data.helpUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tab-button"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Help
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Loading State */}
          {store.loading && (
            <div className="modal-body">
              <div className="loading-state">
                <Loader label="Loading connector configuration..." />
              </div>
            </div>
          )}

          {/* Error Banner */}
          {store.error && (
            <div className="error-banner">
              <span className="error-icon"></span>
              <span>{store.error}</span>
            </div>
          )}

          {/* Form Content — tabs are now in the header above */}
          {!store.loading && (store.formSchema || store.connector || !!processElementId || store.visibleTabs.length > 0) && (
            <>
              <div className="modal-body">
                <div className="form-container">
                  {/* Configuration Tab — left sidebar + right content area */}
                  {store.activeTab === 'configuration' && (
                    <div className="config-form-layout">
                      {/* Left sidebar: vertical tabs (only when multiple forms) */}
                      {store.configForms.length > 1 && (
                        <div className="config-form-sidebar">
                          {ENABLE_FORM_LABEL_MENU ? (
                            <FormLabelMenu
                              forms={store.configForms}
                              activeFormId={store.activeConfigFormId}
                              getDisplayTitle={getFormDisplayTitle}
                              onSelect={handleConfigFormTabChange}
                            />
                          ) : (
                            <FlatFormTabList
                              forms={store.configForms}
                              activeFormId={store.activeConfigFormId}
                              getDisplayTitle={getFormDisplayTitle}
                              onSelect={handleConfigFormTabChange}
                            />
                          )}

                          {/* Form designer link — tenant-admin only */}
                          {canDesignForms && store.activeConfigFormId != null && (
                            <div className="config-form-sidebar-footer">
                              <button
                                className="form-designer-btn"
                                onClick={() => handleOpenFormDesigner(store.activeConfigFormId!)}
                                title={`Open form ${store.activeConfigFormId} in Form Designer`}
                              >
                                <PencilIcon size={10} />
                                Design Using Form Editor
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Right main area: description at top, then form content */}
                      <div className="config-form-main">
                        {/* Description bar — shown at top when active form has a subtitle */}
                        {store.configForms.length > 1 && (() => {
                          const activeForm = store.configForms.find(f => f.formID === store.activeConfigFormId);
                          return activeForm?.subtitle ? (
                            <div className="config-form-description">{activeForm.subtitle}</div>
                          ) : null;
                        })()}

                        {/* Form designer link for single-form nodes (no sidebar shown) */}
                        {canDesignForms && store.configForms.length <= 1 && store.activeConfigFormId != null && (
                          <div className="config-form-designer-inline">
                            <button
                              className="form-designer-btn"
                              onClick={() => handleOpenFormDesigner(store.activeConfigFormId!)}
                              title={`Open form ${store.activeConfigFormId} in Form Designer`}
                            >
                              <PencilIcon size={10} />
                              Design Using Form Editor
                            </button>
                          </div>
                        )}

                        {/* Form Controls — rendered by FormRenderer (unified atlas-forms engine) */}
                        {(() => {
                          const hasSchema = !!store.formSchema;
                          const hasControls = (store.formSchema?.controls?.length ?? 0) > 0;
                          if (!hasSchema) {
                            console.warn('[ConnectorConfigDialog] FormRenderer: No schema available', { loading: store.loading });
                          } else if (!hasControls) {
                            const schemaKeys = store.formSchema ? Object.keys(store.formSchema) : [];
                            const controlsValue = store.formSchema?.controls;
                            const controlsIsArray = Array.isArray(controlsValue);
                            const controlsIsEmpty = controlsIsArray && controlsValue.length === 0;
                            const controlsIsNull = controlsValue === null;
                            const controlsIsUndefined = controlsValue === undefined;

                            // Find which form in configForms this schema came from
                            const matchingForm = store.configForms.find(f => f.schema === store.formSchema);
                            const formSource = matchingForm ? {
                              formID: matchingForm.formID,
                              formName: matchingForm.formName,
                              formCode: matchingForm.formCode,
                              source: matchingForm.source,
                              displayOrder: matchingForm.displayOrder,
                            } : { found: false };

                            console.error(
                              '%c[ConnectorConfigDialog] ❌ FormRenderer: Schema exists but has no controls',
                              'color: red; font-weight: bold; font-size: 14px;'
                            );

                            console.group('%c📋 FORM IDENTIFICATION', 'color: #ff6b6b; font-weight: bold;');
                            console.error('Active Rendering Form ID:', store.activeConfigFormId);
                            console.error('Matching Source Form:', matchingForm ? {
                              formID: matchingForm.formID,
                              formName: matchingForm.formName,
                              formCode: matchingForm.formCode,
                              source: matchingForm.source,
                            } : 'NOT FOUND IN CONFIGFORMS');
                            console.error('Node Process Element ID:', store.processElementId);
                            console.error('Connector ID:', store.connectorId);
                            console.groupEnd();

                            console.group('%c🔍 EXPECTED SCHEMA STRUCTURE', 'color: #4ecdc4; font-weight: bold;');
                            console.error('Expected Path: schema.controls[] (array of FormControl objects)');
                            console.error('Expected Format:', {
                              controls: [
                                { id: 'fieldId', type: 'text', label: 'Field Label', required: true },
                                { id: 'fieldId2', type: 'select', label: 'Select Field', options: [] },
                              ],
                              formName: 'Form Name',
                              title: 'Form Title',
                            });
                            console.error('Documentation: https://atlas-forms-docs/schema');
                            console.groupEnd();

                            console.group('%c⚠️ ACTUAL SCHEMA STRUCTURE', 'color: #ff9ff3; font-weight: bold;');
                            console.error('Schema Keys:', schemaKeys);
                            console.error('Schema Type:', typeof store.formSchema);
                            console.error('Full Schema Object:', store.formSchema);
                            console.error('Pretty JSON:\n', JSON.stringify(store.formSchema, null, 2));
                            console.groupEnd();

                            console.group('%c🔧 CONTROLS ANALYSIS', 'color: #ffa502; font-weight: bold;');
                            console.error('Controls Value:', controlsValue);
                            console.error('Controls Type:', typeof controlsValue);
                            console.error('Is Array?', controlsIsArray);
                            console.error('Is Empty Array?', controlsIsEmpty);
                            console.error('Is Null?', controlsIsNull);
                            console.error('Is Undefined?', controlsIsUndefined);
                            console.error('Controls Length:', controlsValue?.length ?? 'N/A');
                            console.error('Controls Type Check:', {
                              isArray: Array.isArray(controlsValue),
                              length: controlsValue?.length,
                              hasValues: controlsIsArray && controlsValue.length > 0,
                            });
                            console.groupEnd();

                            console.group('%c🏗️ SCHEMA PROPERTIES FOUND', 'color: #a8e6cf; font-weight: bold;');
                            console.error('All Properties:', schemaKeys);
                            console.error('Property Check:', {
                              hasControls: 'controls' in (store.formSchema || {}),
                              hasFormName: 'formName' in (store.formSchema || {}),
                              hasTitle: 'title' in (store.formSchema || {}),
                              hasLayoutSections: 'layoutSections' in (store.formSchema || {}),
                              hasValidationRules: 'validationRules' in (store.formSchema || {}),
                              hasPages: 'pages' in (store.formSchema || {}),
                              hasFields: 'fields' in (store.formSchema || {}),
                            });
                            console.groupEnd();

                            console.group('%c📡 API RESPONSE CONTEXT', 'color: #ffd3b6; font-weight: bold;');
                            console.error('All Config Forms (API Response):');
                            store.configForms.forEach((f, idx) => {
                              console.error(`  [${idx}] formID=${f.formID}, name="${f.formName}", code="${f.formCode}", source="${f.source}"`, {
                                hasSchema: !!f.schema,
                                schemaKeys: f.schema ? Object.keys(f.schema) : [],
                                hasControls: f.schema?.controls?.length > 0,
                                controlCount: f.schema?.controls?.length ?? 0,
                                schemaJSON: f.schema ? JSON.stringify(f.schema, null, 2) : 'no schema',
                              });
                            });
                            console.groupEnd();

                            console.group('%c🚨 DEBUGGING STEPS', 'color: #e0aaff; font-weight: bold;');
                            console.error('1. Check API Response: Verify nodeFormApiClient.getNodeFormsBySubUsage() returned forms with valid schemas');
                            console.error('2. Check Form Mapping: Line 357 in ConnectorConfigDialog: schema = forms[0].schema');
                            console.error('3. Check API Endpoint: POST /api/nodeforms/getBySubUsage with processElementId=' + store.processElementId);
                            console.error('4. Check NodeFormDto.schema: Ensure backend is returning forms[].schema with controls[] array');
                            console.error('5. Current Issue: Store has active formID=' + store.activeConfigFormId + ' but schema.controls is empty/missing');
                            console.groupEnd();
                          } else {
                            console.log('[ConnectorConfigDialog] ✅ FormRenderer: Rendering with valid schema', { controlCount: hasControls ? store.formSchema!.controls!.length : 0 });
                          }

                          return hasSchema && hasControls ? (
                            <FormThemeProvider theme={theme}>
                              <div className="atlas-form-container">
                                <FormRenderer
                                  key={store.activeConfigFormId ?? 'default'}
                                  schema={store.formSchema as any}
                                  mode="edit"
                                  initialValues={decodedInitialValues}
                                  onValuesChange={handleFormValuesChange}
                                  nested={false}
                                  enableCsrfProtection={false}
                                  showValidationSummary={false}
                                />
                              </div>
                            </FormThemeProvider>
                          ) : store.loading ? (
                            <div className="no-form-placeholder">
                              <Loader size="sm" />
                              Loading configuration form...
                            </div>
                          ) : (
                            <div className="no-form-placeholder">
                              No configuration form has been set up for this node type yet.
                            </div>
                          );
                        })()}

                        {/* Agent Function metadata — shown only on the "Node General Settings" form */}
                        {store.visibleTabs.includes('agentFunction') && (() => {
                          const activeForm = store.configForms.find(f => f.formID === store.activeConfigFormId);
                          return (
                            activeForm?.title === 'Node General Settings' ||
                            activeForm?.formName?.toLowerCase().includes('node-general') ||
                            activeForm?.formCode?.toLowerCase().includes('node-general') ||
                            (activeForm?.source === 'Common' && activeForm?.subtitle?.toLowerCase().includes('common settings'))
                          );
                        })() && (
                          <div className="documentation-panel" style={{ marginTop: 16 }}>
                            <div className="doc-field-group">
                              <label className="doc-field-label">Display Name</label>
                              <input
                                type="text"
                                className="doc-field-input"
                                value={store.agentFunctionDisplayName}
                                onChange={(e) => store.setAgentFunctionDisplayName(e.target.value)}
                                placeholder="Display name"
                              />
                            </div>
                            <div className="doc-field-group">
                              <label className="doc-field-label">Display Order</label>
                              <input
                                type="number"
                                className="doc-field-input"
                                value={store.agentFunctionDisplayOrder}
                                onChange={(e) => store.setAgentFunctionDisplayOrder(parseInt(e.target.value, 10) || 0)}
                                placeholder="0"
                                min={0}
                              />
                            </div>
                            <div className="doc-field-group">
                              <label className="doc-field-label">Label</label>
                              <input
                                type="text"
                                className="doc-field-input"
                                value={store.agentFunctionLabel}
                                onChange={(e) => store.setAgentFunctionLabel(e.target.value)}
                                placeholder="Label"
                              />
                            </div>
                            <div className="doc-field-group">
                              <label className="doc-field-label">Flow Path</label>
                              <input
                                type="text"
                                className="doc-field-input"
                                value={store.agentFunctionFlowPath}
                                onChange={(e) => store.setAgentFunctionFlowPath(e.target.value)}
                                placeholder="e.g. /flows/my-flow"
                              />
                            </div>
                            <div className="doc-field-group">
                              <label className="doc-field-label">Alias</label>
                              <input
                                type="text"
                                className="doc-field-input"
                                value={store.agentFunctionAlias}
                                onChange={(e) => store.setAgentFunctionAlias(e.target.value)}
                                placeholder="Alias"
                              />
                            </div>
                            <div className="doc-field-group">
                              <label className="doc-field-label">Profile Name</label>
                              <input
                                type="text"
                                className="doc-field-input"
                                value={store.agentFunctionProfileName}
                                onChange={(e) => store.setAgentFunctionProfileName(e.target.value)}
                                placeholder="Profile name"
                              />
                            </div>
                          </div>
                        )}

                        {/* Test Result Display */}
                        {store.testResult && (
                          <div
                            className={`test-result ${
                              store.testResult.StatusCode && store.testResult.StatusCode >= 200 && store.testResult.StatusCode < 300
                                ? 'test-result-success'
                                : 'test-result-error'
                            }`}
                          >
                            <div className="test-result-header">
                              <span className="test-result-title">
                                {store.testResult.StatusCode && store.testResult.StatusCode >= 200 && store.testResult.StatusCode < 300
                                  ? 'Connection Successful'
                                  : 'Connection Failed'}
                              </span>
                            </div>
                            <div className="test-result-details">
                              <div className="test-result-metric">
                                <span className="test-result-label">Response Time:</span>
                                <span className="test-result-value">{store.testResult.ResponseTimeMs}ms</span>
                              </div>
                              {store.testResult.StatusCode && (
                                <div className="test-result-metric">
                                  <span className="test-result-label">Status Code:</span>
                                  <span className="test-result-value">{store.testResult.StatusCode}</span>
                                </div>
                              )}
                              {store.testResult.Details?.error && (
                                <div className="test-result-error">{store.testResult.Details.error}</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Credentials Tab - Only render if visible.
                      Migrated from flow-studio's own CredentialSelector/CredentialSection to
                      @bizfirst/ai-agent-admin-ui's CredentialPicker (2026-08-19,
                      octopus-admin-spec fast-follow) — one component merges both the type-agnostic
                      and type-aware behavior via the optional acceptedCredentialTypes prop. */}
                  {store.activeTab === 'credentials' && store.visibleTabs.includes('credentials') && (
                    <div className="credential-selector-section">
                      <CredentialPicker
                        acceptedCredentialTypes={resolvedCredentialTypes}
                        value={store.credentialID}
                        onChange={(credentialId) => store.setCredentialID(credentialId)}
                        label="Credential"
                        placeholder="Select or create a credential..."
                      />
                    </div>
                  )}

                  {/* Agent Tab - Only render if visible.
                      Migrated from flow-studio's own inline search/dropdown + AgentEditor modal to
                      @bizfirst/ai-agent-admin-ui's AgentPicker (2026-08-19, octopus-admin-spec
                      fast-follow) — same search/create/edit behavior, plus delete (never wired here
                      before). store.agentId remains the single source of truth used by the rest of
                      this file's save/config-resolution logic; AgentPicker is a controlled component
                      over it via value/onChange. */}
                  {store.activeTab === 'agent' && store.visibleTabs.includes('agent') && (
                    <div className="entity-selector-section">
                      <AgentPicker
                        value={store.agentId}
                        onChange={(agentId) => store.setAgentId(agentId)}
                        label="Agent"
                        placeholder="Search agents..."
                      />
                    </div>
                  )}

                  {/* Identity Tab - DID signing/encryption keys + trusted-DID allow-lists, managed
                      inline here rather than in a separate app. Replaces the raw-JSON textareas
                      Atlas_Forms_12018/12016 used before the table-based redesign. Needs a saved
                      ProcessElementID to scope queries — a brand-new, not-yet-saved node has none
                      yet, so show a save-first prompt instead of querying with an invalid id. */}
                  {store.activeTab === 'identity' && store.visibleTabs.includes('identity') && (
                    <div className="entity-selector-section">
                      {!processElementId && (
                        <div className="aaa-entity-dropdown-empty">Save this node once before configuring its identity keys and trusted DIDs.</div>
                      )}
                      {processElementId && (
                        <>
                          <div className="identity-tab-header" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                            <a
                              href={`${DID_ADMIN_APP_URL}/node-identity?nodeId=${processElementId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open in full app ↗
                            </a>
                          </div>
                          <h3 style={{ marginTop: 0 }}>Signing &amp; Encryption Keys</h3>
                          <IdentityKeyManager nodeId={processElementId} />
                          <h3>Trusted DIDs</h3>
                          <TrustedDIDManager nodeId={processElementId} />
                        </>
                      )}
                    </div>
                  )}

                  {/* Knowledge Base Tab - Only render if visible */}
                  {store.activeTab === 'knowledgeBase' && store.visibleTabs.includes('knowledgeBase') && (
                    <div className="entity-selector-section">
                      <label className="entity-label">Search Knowledge Base</label>
                      {store.kbError && (
                        <div className="error-message" style={{
                          color: '#ef4444',
                          fontSize: '13px',
                          marginBottom: '8px',
                          padding: '8px',
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          borderRadius: '4px'
                        }}>
                          {store.kbError}
                        </div>
                      )}
                      <div className="entity-selector-group entity-search-group">
                        <input
                          type="text"
                          className="entity-search-input"
                          placeholder="Search knowledge bases..."
                          value={store.kbSearch}
                          onChange={(e) => filterKnowledgeBases(e.target.value)}
                          disabled={store.loadingKBs}
                        />
                        <button
                          type="button"
                          className="entity-dropdown-btn"
                          onClick={() => store.kbShowResults ? store.setKbShowResults(false) : loadKnowledgeBases()}
                          title={store.kbShowResults ? 'Close' : 'Show all knowledge bases'}
                          disabled={store.loadingKBs}
                        >
                          <ChevronDownIcon size={14} style={{ transform: store.kbShowResults ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                        </button>
                        <button
                          className="entity-edit-btn" type="button"
                          onClick={() => { if (store.knowledgeBaseId) { setEntityDialogAction('edit'); store.setOpenAddDialog('knowledgeBase'); } }}
                          disabled={!store.knowledgeBaseId || store.loadingKBs}
                          title="Edit knowledge base"
                        >
                          <PencilIcon size={13} />
                        </button>
                        <button
                          type="button"
                          className="entity-add-btn"
                          onClick={() => { setEntityDialogAction('create'); store.setOpenAddDialog('knowledgeBase'); }}
                          title="Add new knowledge base"
                        >
                          Create
                        </button>

                        {/* Selected Knowledge Base — layer between search row and dropdown */}
                        {store.knowledgeBaseId && store.knowledgeBases.find((kb) => kb.KnowledgeBaseID === store.knowledgeBaseId) && !store.loadingKBs && (
                          <div className="entity-info">
                            <span className="entity-icon"><DocumentIcon size={16} /></span>
                            <span className="entity-name">{store.knowledgeBases.find((kb) => kb.KnowledgeBaseID === store.knowledgeBaseId)?.Name}</span>
                            <span className="entity-id">(ID: {store.knowledgeBaseId})</span>
                          </div>
                        )}

                        {/* Knowledge Base Results Dropdown */}
                        {store.kbShowResults && (
                          <div className="entity-results-dropdown">
                            {store.loadingKBs && <div className="entity-result-item loading"><SearchIcon size={14} /> Loading...</div>}
                            {!store.loadingKBs && filteredKnowledgeBases.length === 0 && (
                              <div className="entity-result-item no-results">No knowledge bases found</div>
                            )}
                            {!store.loadingKBs &&
                              filteredKnowledgeBases.map((kb) => (
                                <div
                                  key={kb.KnowledgeBaseID}
                                  className={`entity-result-item ${kb.KnowledgeBaseID === store.knowledgeBaseId ? 'selected' : ''}`}
                                  onClick={() => {
                                    store.setKnowledgeBaseId(kb.KnowledgeBaseID);
                                    store.setKbShowResults(false);
                                    store.setKbSearch('');
                                  }}
                                >
                                  <span className="entity-icon"><DocumentIcon size={16} /></span>
                                  <span className="entity-name">{kb.Name}</span>
                                  <span className="entity-id">(ID: {kb.KnowledgeBaseID})</span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Channel Tab - Only render if visible */}
                  {store.activeTab === 'channel' && store.visibleTabs.includes('channel') && (
                    <div className="entity-selector-section">
                      <label className="entity-label">Search Channel</label>
                      {store.channelError && (
                        <div className="error-message" style={{
                          color: '#ef4444',
                          fontSize: '13px',
                          marginBottom: '8px',
                          padding: '8px',
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          borderRadius: '4px'
                        }}>
                          {store.channelError}
                        </div>
                      )}
                      <div className="entity-selector-group entity-search-group">
                        <input
                          type="text"
                          className="entity-search-input"
                          placeholder="Search channels..."
                          value={store.channelSearch}
                          onChange={(e) => filterChannels(e.target.value)}
                          disabled={store.loadingChannels}
                        />
                        <button
                          type="button"
                          className="entity-dropdown-btn"
                          onClick={() => store.channelShowResults ? store.setChannelShowResults(false) : loadChannels()}
                          title={store.channelShowResults ? 'Close' : 'Show all channels'}
                          disabled={store.loadingChannels}
                        >
                          <ChevronDownIcon size={14} style={{ transform: store.channelShowResults ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                        </button>
                        <button
                          className="entity-edit-btn" type="button"
                          onClick={() => { if (store.channelId) { setEntityDialogAction('edit'); store.setOpenAddDialog('channel'); } }}
                          disabled={!store.channelId || store.loadingChannels}
                          title="Edit channel"
                        >
                          <PencilIcon size={13} />
                        </button>
                        <button
                          type="button"
                          className="entity-add-btn"
                          onClick={() => { setEntityDialogAction('create'); store.setOpenAddDialog('channel'); }}
                          title="Add new channel"
                        >
                          Create
                        </button>

                        {/* Selected Channel — layer between search row and dropdown */}
                        {store.channelId && store.channels.find((c) => c.ChannelID === store.channelId) && !store.loadingChannels && (
                          <div className="entity-info">
                            <span className="entity-icon"><GlobeIcon size={16} /></span>
                            <span className="entity-name">{
                              (() => {
                                const channel = store.channels.find((c) => c.ChannelID === store.channelId);
                                return channel?.ChannelName || channel?.Name || 'N/A';
                              })()
                            }</span>
                            <span className="entity-id">(ID: {store.channelId})</span>
                          </div>
                        )}

                        {/* Channel Results Dropdown */}
                        {store.channelShowResults && (
                          <div className="entity-results-dropdown">
                            {store.loadingChannels && <div className="entity-result-item loading"><SearchIcon size={14} /> Loading...</div>}
                            {!store.loadingChannels && filteredChannels.length === 0 && (
                              <div className="entity-result-item no-results">No channels found</div>
                            )}
                            {!store.loadingChannels &&
                              filteredChannels.map((channel) => (
                                <div
                                  key={channel.ChannelID}
                                  className={`entity-result-item ${channel.ChannelID === store.channelId ? 'selected' : ''}`}
                                  onClick={() => {
                                    store.setChannelId(channel.ChannelID);
                                    store.setChannelShowResults(false);
                                    store.setChannelSearch('');
                                  }}
                                >
                                  <span className="entity-icon"><GlobeIcon size={16} /></span>
                                  <span className="entity-name">{channel.ChannelName || channel.Name || 'N/A'}</span>
                                  <span className="entity-id">(ID: {channel.ChannelID})</span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* LLM Model Tab - Only render if visible */}
                  {store.activeTab === 'llmModel' && store.visibleTabs.includes('llmModel') && (
                    <div className="entity-selector-section">
                      <label className="entity-label">Search LLM Model</label>
                      {store.llmError && (
                        <div className="error-message" style={{
                          color: '#ef4444',
                          fontSize: '13px',
                          marginBottom: '8px',
                          padding: '8px',
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          borderRadius: '4px'
                        }}>
                          {store.llmError}
                        </div>
                      )}
                      <div className="entity-selector-group entity-search-group">
                        <input
                          type="text"
                          className="entity-search-input"
                          placeholder="Search LLM models..."
                          value={store.llmSearch}
                          onChange={(e) => filterLLMModels(e.target.value)}
                          disabled={store.loadingLLMs}
                        />
                        <button
                          type="button"
                          className="entity-dropdown-btn"
                          onClick={() => store.llmShowResults ? store.setLlmShowResults(false) : loadLLMModels()}
                          title={store.llmShowResults ? 'Close' : 'Show all LLM models'}
                          disabled={store.loadingLLMs}
                        >
                          <ChevronDownIcon size={14} style={{ transform: store.llmShowResults ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                        </button>
                        <button
                          className="entity-edit-btn" type="button"
                          onClick={() => { if (store.llmModelId) { setEntityDialogAction('edit'); store.setOpenAddDialog('llmModel'); } }}
                          disabled={!store.llmModelId || store.loadingLLMs}
                          title="Edit LLM model"
                        >
                          <PencilIcon size={13} />
                        </button>
                        <button
                          type="button"
                          className="entity-add-btn"
                          onClick={() => { setEntityDialogAction('create'); store.setOpenAddDialog('llmModel'); }}
                          title="Add new LLM model"
                        >
                          Create
                        </button>

                        {/* Selected LLM Model — layer between search row and dropdown */}
                        {store.llmModelId && store.llmModels.find((m) => m.LlmModelID === store.llmModelId) && !store.loadingLLMs && (
                          <div className="entity-info">
                            <span className="entity-icon"><BrainIcon size={16} /></span>
                            <span className="entity-name">{
                              (() => {
                                const model = store.llmModels.find((m) => m.LlmModelID === store.llmModelId);
                                return model?.ModelName || model?.Name || 'N/A';
                              })()
                            }</span>
                            <span className="entity-id">(ID: {store.llmModelId})</span>
                          </div>
                        )}

                        {/* LLM Model Results Dropdown */}
                        {store.llmShowResults && (
                          <div className="entity-results-dropdown">
                            {store.loadingLLMs && <div className="entity-result-item loading"><SearchIcon size={14} /> Loading...</div>}
                            {!store.loadingLLMs && filteredLLMModels.length === 0 && (
                              <div className="entity-result-item no-results">No LLM models found</div>
                            )}
                            {!store.loadingLLMs &&
                              filteredLLMModels.map((model) => (
                                <div
                                  key={model.LlmModelID}
                                  className={`entity-result-item ${model.LlmModelID === store.llmModelId ? 'selected' : ''}`}
                                  onClick={() => {
                                    store.setLlmModelId(model.LlmModelID);
                                    store.setLlmShowResults(false);
                                    store.setLlmSearch('');
                                  }}
                                >
                                  <span className="entity-icon"><BrainIcon size={16} /></span>
                                  <span className="entity-name">{model.ModelName || model.Name || 'N/A'}</span>
                                  <span className="entity-id">(ID: {model.LlmModelID})</span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}


                  {/* Agent Function Tab - Only render if visible */}
                  {store.activeTab === 'agentFunction' && store.visibleTabs.includes('agentFunction') && (
                    <div className="entity-selector-section">
                      <label className="entity-label">Search Agent Function</label>
                      {store.functionError && (
                        <div className="error-message" style={{
                          color: '#ef4444',
                          fontSize: '13px',
                          marginBottom: '8px',
                          padding: '8px',
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          borderRadius: '4px'
                        }}>
                          {store.functionError}
                        </div>
                      )}
                      <div className="entity-selector-group entity-search-group">
                        <input
                          type="text"
                          className="entity-search-input"
                          placeholder="Search agent functions..."
                          value={store.functionSearch}
                          onChange={(e) => filterAgentFunctions(e.target.value)}
                          disabled={store.loadingFunctions}
                        />
                        <button
                          type="button"
                          className="entity-dropdown-btn"
                          onClick={() => store.functionShowResults ? store.setFunctionShowResults(false) : loadAgentFunctions()}
                          title={store.functionShowResults ? 'Close' : 'Show all agent functions'}
                          disabled={store.loadingFunctions}
                        >
                          <ChevronDownIcon size={14} style={{ transform: store.functionShowResults ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                        </button>
                        <button
                          className="entity-edit-btn" type="button"
                          onClick={() => { if (store.agentFunctionId) { setEntityDialogAction('edit'); store.setOpenAddDialog('agentFunction'); } }}
                          disabled={!store.agentFunctionId || store.loadingFunctions}
                          title="Edit agent function"
                        >
                          <PencilIcon size={13} />
                        </button>
                        <button
                          type="button"
                          className="entity-add-btn"
                          onClick={() => { setEntityDialogAction('create'); store.setOpenAddDialog('agentFunction'); }}
                          title="Add new agent function"
                        >
                          Create
                        </button>

                        {/* Selected Agent Function — layer between search row and dropdown */}
                        {store.agentFunctionId && store.agentFunctions.find((f) => f.AgentFunctionID === store.agentFunctionId) && !store.loadingFunctions && (
                          <div className="entity-info">
                            <ZapIcon size={16} />
                            <span className="entity-name">{store.agentFunctions.find((f) => f.AgentFunctionID === store.agentFunctionId)?.Name}</span>
                            <span className="entity-id">(ID: {store.agentFunctionId})</span>
                          </div>
                        )}

                        {/* Agent Function Results Dropdown */}
                        {store.functionShowResults && (
                          <div className="entity-results-dropdown">
                            {store.loadingFunctions && <div className="entity-result-item loading"><SearchIcon size={14} /> Loading...</div>}
                            {!store.loadingFunctions && filteredAgentFunctions.length === 0 && (
                              <div className="entity-result-item no-results">No agent functions found</div>
                            )}
                            {!store.loadingFunctions &&
                              filteredAgentFunctions.map((func) => (
                                <div
                                  key={func.AgentFunctionID}
                                  className={`entity-result-item ${func.AgentFunctionID === store.agentFunctionId ? 'selected' : ''}`}
                                  onClick={() => {
                                    store.setAgentFunctionId(func.AgentFunctionID);
                                    store.setFunctionShowResults(false);
                                    store.setFunctionSearch('');
                                  }}
                                >
                                  <ZapIcon size={16} />
                                  <span className="entity-name">{func.Name}</span>
                                  <span className="entity-id">(ID: {func.AgentFunctionID})</span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Memory Tab */}
                  {store.activeTab === 'memory' && store.visibleTabs.includes('memory') && (() => {
                    const MEMORY_TYPES = [
                      { value: 'bizfirst', label: 'BizFirst (Default)' },
                      { value: 'custom', label: 'Custom' },
                    ];
                    const filteredMemoryTypes = memorySearch.trim()
                      ? MEMORY_TYPES.filter((t) => t.label.toLowerCase().includes(memorySearch.toLowerCase()))
                      : MEMORY_TYPES;
                    const selectedMemoryLabel = MEMORY_TYPES.find((t) => t.value === store.memoryType)?.label ?? '';
                    return (
                      <div className="entity-selector-section">
                        <label className="entity-label">Memory Configuration</label>
                        <div className="entity-selector-group entity-search-group">
                          <input
                            type="text"
                            className="entity-search-input"
                            placeholder="Search memory type..."
                            value={memorySearch}
                            onChange={(e) => { setMemorySearch(e.target.value); setMemoryShowResults(true); }}
                          />
                          <button
                            type="button"
                            className="entity-dropdown-btn"
                            onClick={() => setMemoryShowResults(!memoryShowResults)}
                            title={memoryShowResults ? 'Close' : 'Show memory types'}
                          >
                            <ChevronDownIcon size={14} style={{ transform: memoryShowResults ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                          </button>

                          {/* Selected Memory Type — layer between search row and dropdown */}
                          {selectedMemoryLabel && (
                            <div className="entity-info">
                              <span className="entity-icon"><LightbulbIcon size={16} /></span>
                              <span className="entity-name">{selectedMemoryLabel}</span>
                            </div>
                          )}

                          {memoryShowResults && (
                            <div className="entity-results-dropdown">
                              {filteredMemoryTypes.length === 0 && (
                                <div className="entity-result-item no-results">No types found</div>
                              )}
                              {filteredMemoryTypes.map((type) => (
                                <div
                                  key={type.value}
                                  className={`entity-result-item ${store.memoryType === type.value ? 'selected' : ''}`}
                                  onClick={() => {
                                    store.setMemoryType(type.value as 'bizfirst' | 'custom');
                                    setMemoryShowResults(false);
                                    setMemorySearch('');
                                  }}
                                >
                                  <span className="entity-icon"><LightbulbIcon size={16} /></span>
                                  <span className="entity-name">{type.label}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Documentation Tab */}
                  {store.activeTab === 'documentation' && store.visibleTabs.includes('documentation') && (
                    <div className="documentation-panel">
                      <div className="doc-field-group">
                        <label className="doc-field-label">Label</label>
                        <input
                          type="text"
                          className="doc-field-input"
                          value={store.docLabel}
                          onChange={(e) => store.setDocLabel(e.target.value)}
                          placeholder="Node label shown on canvas"
                        />
                      </div>
                      <div className="doc-field-group">
                        <label className="doc-field-label">Description</label>
                        <textarea
                          className="doc-field-textarea"
                          value={store.docDescription}
                          onChange={(e) => store.setDocDescription(e.target.value)}
                          placeholder="What does this node do?"
                          rows={4}
                        />
                      </div>
                    </div>
                  )}

                  {/* Primary Config (Info) Tab */}
                  {store.activeTab === 'primaryConfig' && store.visibleTabs.includes('primaryConfig') && (
                    <div className="primary-config-panel">
                      <table className="info-table">
                        <tbody>
                          <tr>
                            <td className="info-table-key">Node Type</td>
                            <td><code className="info-table-value">{nodeInfo?.type ?? '—'}</code></td>
                          </tr>
                          <tr>
                            <td className="info-table-key">Category</td>
                            <td><code className="info-table-value">{nodeInfo?.data.category ?? '—'}</code></td>
                          </tr>
                          <tr>
                            <td className="info-table-key">Node Usage</td>
                            <td><code className="info-table-value">DesignerConfigureNode</code></td>
                          </tr>
                          <tr>
                            <td className="info-table-key">Node Sub Usage</td>
                            <td><code className="info-table-value">node-design-configuration</code></td>
                          </tr>
                          <tr>
                            <td className="info-table-key">Process Element ID</td>
                            <td><code className="info-table-value">{nodeInfo?.backendId ?? 'Not saved yet'}</code></td>
                          </tr>
                          <tr>
                            <td className="info-table-key">Connector ID</td>
                            <td><code className="info-table-value">{nodeInfo?.data.connectorId ?? '—'}</code></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Footer / Actions */}
          {!store.loading && (
            <div className="modal-footer dialog-actions">
              <div className="right-actions">
                <button className="btn btn-default" onClick={onClose}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={store.saving}
                >
                  {store.saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Entity Add Dialogs.
          Agent's own create/edit dialog is gone from here — AgentPicker (Agent tab, above) now
          owns its own editor modal internally, sourced from @bizfirst/ai-agent-admin-ui. */}
      <KnowledgeBaseEditor
        isOpen={store.openAddDialog === 'knowledgeBase'}
        onClose={() => store.setOpenAddDialog(null)}
        onKBSaved={handleKnowledgeBaseAdded}
        kbId={entityDialogAction === 'edit' ? store.knowledgeBaseId || undefined : undefined}
      />

      <ChannelEditor
        isOpen={store.openAddDialog === 'channel'}
        onClose={() => store.setOpenAddDialog(null)}
        onChannelSaved={handleChannelAdded}
        channelId={entityDialogAction === 'edit' ? store.channelId || undefined : undefined}
      />

      <LLMModelEditor
        isOpen={store.openAddDialog === 'llmModel'}
        onClose={() => store.setOpenAddDialog(null)}
        onLLMModelSaved={handleLLMModelAdded}
        llmModelId={entityDialogAction === 'edit' ? store.llmModelId || undefined : undefined}
      />

      <AIFunctionEditor
        isOpen={store.openAddDialog === 'agentFunction'}
        onClose={() => store.setOpenAddDialog(null)}
        onFunctionSaved={handleAgentFunctionAdded}
        functionId={entityDialogAction === 'edit' ? store.agentFunctionId || undefined : undefined}
      />
    </ModalPortal>
    </AiAgentAdminApiProvider>
  );
};

export default ConnectorConfigDialog;
